/**
 * empathyBalanceEngine.js
 *
 * Calibrates the balance between empathy and clarity in Kevia's response.
 * Goal: pastoral warmth without sentimentalism; clarity without coldness.
 *
 * Balance states:
 *   over_empathy  — response risks becoming sentimental or emotionally dramatic
 *   balanced      — appropriate warmth and directness for the context
 *   over_clarity  — response risks feeling clinical or impersonal
 *
 * The engine does NOT tell Kevia what to feel — it nudges the balance
 * when it detects a structural lean in one direction.
 */

/**
 * @param {{
 *   emotion:   object|null,
 *   intent:    string,
 *   depth:     string,
 *   riskLevel: string,
 *   mood:      string,
 * }} params
 * @returns {{
 *   empathyScore: number,
 *   clarityScore: number,
 *   balance:      "over_empathy"|"balanced"|"over_clarity",
 *   instruction:  string|null,
 * }}
 */
export function assessEmpathyBalance({ emotion, intent, depth, riskLevel, mood }) {
  let empathyScore = 0.50;
  let clarityScore = 0.50;

  // Push empathy up based on emotional load
  if (emotion?.detected) {
    const boost =
      emotion.intensity === "high"   ? 0.30 :
      emotion.intensity === "medium" ? 0.18 : 0.08;
    empathyScore += boost;
  }

  // Grief or pastoral-care context → empathy priority
  if (mood === "grief" || mood === "pastoral-care") empathyScore += 0.20;

  // Analytical and operational intents → clarity priority
  if (intent === "analysis" || intent === "planning")  clarityScore += 0.30;
  if (intent === "sermon"   || intent === "reflection") clarityScore += 0.15;
  if (intent === "visit_plan" || intent === "events")   clarityScore += 0.10;

  // Critical / high risk → safety before efficiency (more empathy, less cold analysis)
  if (riskLevel === "critical" || riskLevel === "high") {
    empathyScore += 0.20;
    clarityScore -= 0.10;
  }

  // Deep or reflective depth naturally allows more empathetic space
  if (depth === "reflective") empathyScore += 0.10;

  // Clamp
  empathyScore = Math.min(1, Math.max(0, empathyScore));
  clarityScore = Math.min(1, Math.max(0, clarityScore));

  const diff = empathyScore - clarityScore;

  if (diff > 0.30) {
    return {
      empathyScore,
      clarityScore,
      balance: "over_empathy",
      instruction:
        "Sé cálido, pero no dramatices. Mantén una firmeza pastoral suave sin caer en sentimentalismo.",
    };
  }

  if (diff < -0.30) {
    return {
      empathyScore,
      clarityScore,
      balance: "over_clarity",
      instruction:
        "Antes de ser útil, sé humano. Una nota de empatía no resta precisión pastoral.",
    };
  }

  return { empathyScore, clarityScore, balance: "balanced", instruction: null };
}
